import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

// ─── Filters ────────────────────────────────────────────────────────────────
//
// Shared shape used by both the table and the KPIs endpoint. `sport` and
// `state` accept either `undefined` or the sentinel `"all"` to mean "no
// filter" — both are stripped from the wire payload by `buildParams` below
// so the BE sees an absent key (equivalent to "all" per the contract).

export type SportsbookTopUsersView =
  | "top_winners"
  | "top_losers"
  | "top_turnover";

export type SportsbookTopUsersFilters = {
  start_date: string;
  end_date: string;
  view?: SportsbookTopUsersView;
  limit?: number;
  sport?: string;
  state?: string;
};

const ALL_SENTINEL = "all";

const isAllOrEmpty = (value: string | undefined): boolean =>
  value === undefined || value === "" || value === ALL_SENTINEL;

const buildParams = (
  filters: SportsbookTopUsersFilters,
): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    start_date: filters.start_date,
    end_date: filters.end_date,
  };
  if (filters.view) params.view = filters.view;
  if (typeof filters.limit === "number") params.limit = filters.limit;
  // BE expects bare `<key>=<value>` for single-value filters and treats an
  // absent key (or the literal `"all"`) as "no filter".
  if (!isAllOrEmpty(filters.sport)) params["sport"] = filters.sport as string;
  if (!isAllOrEmpty(filters.state)) params["state"] = filters.state as string;
  return params;
};

// ─── Table endpoint ─────────────────────────────────────────────────────────

export type SportsbookTopUserRow = {
  /** Stable key for the table — the raw `user_id` from the BE. */
  key: string;
  userId: string;
  /**
   * Display label. Falls back to `userId` when the BE returns `null` so
   * the cell is never blank (the link still targets `user_id`).
   */
  username: string;
  turnover: number;
  pnl: number;
  marginPct: number;
};

export type SportsbookTopUsersFilterOption = {
  /** Wire value (e.g. `"nba"`, `"CA"`, `25`). */
  value: string | number;
  /** Human label (e.g. `"NBA"`, `"California"`, `"25 Users"`). */
  label: string;
};

export type SportsbookTopUsersFilterDefinition = {
  /** Filter id from the BE — `view` | `limit` | `sport` | `state`. */
  id: string;
  options: SportsbookTopUsersFilterOption[];
  defaultValues: Array<string | number>;
  selectedValues: Array<string | number>;
};

export type SportsbookTopUsersTableData = {
  rows: SportsbookTopUserRow[];
  filters: SportsbookTopUsersFilterDefinition[];
};

type RawTableRow = {
  user_id?: string | number | null;
  username?: string | null;
  turnover?: number | null;
  pnl?: number | null;
  margin_pct?: number | null;
};

type RawFilterOption = {
  value?: string | number | null;
  label?: string | null;
};

type RawFilterDefinition = {
  /** BE uses `key` today; accept `id` too in case the contract changes. */
  key?: string;
  id?: string;
  multi_select?: boolean;
  /**
   * Options can come back as either flat scalars (`["top_winners", …]` —
   * the current BE shape) or as `{value,label}` objects. Both are
   * normalised to the `{value,label}` shape below.
   */
  options?: Array<string | number | RawFilterOption | null | undefined>;
  cascading_options?: unknown;
  default_values?: Array<string | number>;
  selected_values?: Array<string | number>;
};

type RawTableEnvelope = {
  data?: RawTableRow[] | null;
  meta?: { filters?: RawFilterDefinition[] | null } | null;
};

const toNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normaliseFilterOption = (
  raw: string | number | RawFilterOption | null | undefined,
): SportsbookTopUsersFilterOption | null => {
  if (raw === null || raw === undefined) return null;
  // Flat scalar: BE uses the same string for both value and label
  // (e.g. `"top_winners"`). Callers can humanise it later if needed.
  if (typeof raw === "string" || typeof raw === "number") {
    return { value: raw, label: String(raw) };
  }
  if (typeof raw === "object" && raw.value !== null && raw.value !== undefined) {
    return {
      value: raw.value as string | number,
      label: String(raw.label ?? raw.value),
    };
  }
  return null;
};

const parseFilterDefinitions = (
  raw: RawFilterDefinition[] | null | undefined,
): SportsbookTopUsersFilterDefinition[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      // Filter id arrives as `key` from the BE today. `id` is accepted as
      // a defensive fallback so a future rename doesn't silently drop the
      // entire filter row.
      const id = (entry?.key ?? entry?.id) as string | undefined;
      if (!id) return null;
      const options = Array.isArray(entry.options)
        ? entry.options
            .map(normaliseFilterOption)
            .filter((opt): opt is SportsbookTopUsersFilterOption => opt !== null)
        : [];
      return {
        id,
        options,
        defaultValues: Array.isArray(entry.default_values)
          ? entry.default_values
          : [],
        selectedValues: Array.isArray(entry.selected_values)
          ? entry.selected_values
          : [],
      };
    })
    .filter(
      (entry): entry is SportsbookTopUsersFilterDefinition => entry !== null,
    );
};

const parseTableResponse = (
  body: unknown,
): SportsbookTopUsersTableData => {
  if (!isRecord(body)) return { rows: [], filters: [] };

  // Two envelope shapes are accepted so the parser tolerates both the
  // current flat BE response and a future double-envelope variant
  // (matching how other sportsbook endpoints wrap with `{ data: { … } }`):
  //   • Flat (today):   { data: [...rows], meta: { filters: [...] } }
  //   • Nested:         { data: { data: [...rows], meta: { filters: [...] } } }
  let rawRows: RawTableRow[] = [];
  let rawFilters: RawFilterDefinition[] | null | undefined;

  if (Array.isArray(body.data)) {
    rawRows = body.data as RawTableRow[];
    const meta = (body as RawTableEnvelope).meta;
    rawFilters = meta?.filters;
  } else if (isRecord(body.data)) {
    const nested = body.data as RawTableEnvelope;
    if (Array.isArray(nested.data)) {
      rawRows = nested.data;
      rawFilters = nested.meta?.filters;
    }
  }

  const rows: SportsbookTopUserRow[] = rawRows
    .filter((row): row is RawTableRow => Boolean(row))
    .map((row, index) => {
      const userId = String(row.user_id ?? "").trim();
      const usernameRaw =
        typeof row.username === "string" ? row.username.trim() : "";
      return {
        // BE always returns a `user_id`, but the parser stays
        // defensive across every other field — the `row-${index}`
        // sentinel keeps React keys unique even if a future payload
        // returns rows with both `user_id` and `username` null,
        // which would otherwise collapse to "" and silently merge
        // rows in the DataTable.
        key: userId || usernameRaw || `row-${index}`,
        userId,
        username: usernameRaw || userId,
        turnover: toNumber(row.turnover),
        pnl: toNumber(row.pnl),
        marginPct: toNumber(row.margin_pct),
      };
    });

  return {
    rows,
    filters: parseFilterDefinitions(rawFilters),
  };
};

export const fetchSportsbookTopUsers = async (
  filters: SportsbookTopUsersFilters,
): Promise<SportsbookTopUsersTableData> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.SPORTSBOOK_TOP_USERS, {
    params: buildParams(filters),
  });
  return parseTableResponse(response?.data);
};

export const useGetSportsbookTopUsers = (
  filters: SportsbookTopUsersFilters,
) => {
  const { start_date, end_date, view, limit, sport, state } = filters;

  return useQuery<SportsbookTopUsersTableData, Error>({
    // Cache key includes every wire-affecting param. `sport`/`state` are
    // normalised through `isAllOrEmpty` here so the "all"/"" sentinels
    // collapse into a single cache entry (matching what `buildParams` sends).
    queryKey: [
      "SPORTSBOOK_TOP_USERS",
      start_date,
      end_date,
      view ?? null,
      limit ?? null,
      isAllOrEmpty(sport) ? null : sport,
      isAllOrEmpty(state) ? null : state,
    ],
    queryFn: () => fetchSportsbookTopUsers(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};

// ─── KPIs endpoint ──────────────────────────────────────────────────────────

export type SportsbookTopUsersKpiKey =
  | "turnover"
  | "pnl"
  | "margin_pct"
  | "bets";

export type SportsbookTopUsersKpi = {
  /** Top-N aggregate for the current filter set. */
  value: number;
  /** Client-wide aggregate for the same period/filters. */
  total: number;
  /**
   * `value / total * 100` — share of the client-wide total that the
   * top-N accounts for. `null` when the BE can't compute it (e.g.
   * `total === 0`, or the metric is a rate like Margin % where a
   * share-of-total doesn't apply). Single percentage signal surfaced
   * to the wrapper across every tile, per product.
   *
   * Diverges from the Casino contract, which still surfaces
   * `percentChange` on every tile. The BE may keep sending
   * `percent_change` on the Sportsbook envelope for backwards
   * compatibility — the parser intentionally drops it (mirroring the
   * inverse decision we had here before product flipped).
   */
  pctOfTotal: number | null;
};

export type SportsbookTopUsersKpisData = Record<
  SportsbookTopUsersKpiKey,
  SportsbookTopUsersKpi
>;

type RawKpiEntry = {
  value?: number | null;
  total?: number | null;
  /** Share of the client-wide total that top-N accounts for. Surfaced as `pctOfTotal`. */
  pct_of_total?: number | null;
  /**
   * Deviation of top-N from the client-wide total. The BE may still
   * send it for backwards compatibility, but the parser intentionally
   * drops it — `pct_of_total` is the single percentage signal product
   * wants on this report.
   */
  percent_change?: number | null;
};

type RawKpisResponse = {
  data?: Partial<
    Record<SportsbookTopUsersKpiKey, RawKpiEntry | null>
  > | null;
};

const KPI_KEYS: ReadonlyArray<SportsbookTopUsersKpiKey> = [
  "turnover",
  "pnl",
  "margin_pct",
  "bets",
];

const EMPTY_KPI: SportsbookTopUsersKpi = {
  value: 0,
  total: 0,
  pctOfTotal: null,
};

const parseKpisResponse = (body: unknown): SportsbookTopUsersKpisData => {
  const envelope = body as RawKpisResponse | null | undefined;
  const data =
    (envelope?.data as
      | Partial<Record<SportsbookTopUsersKpiKey, RawKpiEntry | null>>
      | null
      | undefined) ??
    null;

  const result = {} as SportsbookTopUsersKpisData;
  for (const key of KPI_KEYS) {
    const raw = data?.[key];
    // BE contract: any entry can come back as `null` — fall through to
    // the empty placeholder so the tile renders zeros instead of
    // crashing on a null property access.
    if (!raw || typeof raw !== "object") {
      result[key] = { ...EMPTY_KPI };
      continue;
    }
    result[key] = {
      value: toNumber(raw.value),
      total: toNumber(raw.total),
      // `pct_of_total` is intentionally `null` (not `0`) when the BE
      // can't compute it (e.g. `total === 0`, or the metric is a rate
      // like Margin %). Preserving the distinction lets the wrapper
      // hide the badge instead of showing a misleading "0%".
      pctOfTotal:
        typeof raw.pct_of_total === "number" &&
        Number.isFinite(raw.pct_of_total)
          ? raw.pct_of_total
          : null,
    };
  }
  return result;
};

export const fetchSportsbookTopUsersKpis = async (
  filters: SportsbookTopUsersFilters,
): Promise<SportsbookTopUsersKpisData> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.SPORTSBOOK_TOP_USERS_KPIS, {
    params: buildParams(filters),
  });
  return parseKpisResponse(response?.data);
};

export const useGetSportsbookTopUsersKpis = (
  filters: SportsbookTopUsersFilters,
) => {
  const { start_date, end_date, view, limit, sport, state } = filters;

  return useQuery<SportsbookTopUsersKpisData, Error>({
    queryKey: [
      "SPORTSBOOK_TOP_USERS_KPIS",
      start_date,
      end_date,
      view ?? null,
      limit ?? null,
      isAllOrEmpty(sport) ? null : sport,
      isAllOrEmpty(state) ? null : state,
    ],
    queryFn: () => fetchSportsbookTopUsersKpis(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
